import { useEffect, useMemo, useRef, useState } from "react";

import { Shipment } from "../models/Shipment";
import { useApiProvider } from "../providers/api/apiProvider";
import { useLocation } from "react-router-dom";
import { getShipments, GetShipmentsPayload } from "../services/shipmentService";

export const useShipmentController = () => {
  const {
    pathname,
  } = useLocation();

  const {
    paginatedApiCall
  } = useApiProvider();

  const [ page, setPage ] = useState<number>( 1 );
  const [ perPage, setPerPage ] = useState<number>( 15 );
  const [ total, setTotal ] = useState<number>( 0 );
  const [ search, setSearch ] = useState<string>( "" );

  const [ shipments, setShipments ] = useState<Array<Shipment>>([]);
  const [ loadingShipments, setLoadingShipments ] = useState<boolean>( false );

  const [ shipment, setShipment ] = useState<Shipment | null>( null );

  const [ openShipmentModal, setOpenShipmentModal ] = useState<boolean>( false );
  const didMountModalEffect = useRef<boolean>( false );

  const handleShipment = ( shipment: Shipment | null ) => {
    setShipment( shipment );

    if( Boolean( shipment )) setOpenShipmentModal( true );
  };

  const changePage = ( page: number ) => setPage( page );

  const changeSearch = ( value: string ) => {
    setSearch( value );
    setPage( 1 );
  };

  const loadShipments = async () => {
    const response = await paginatedApiCall<GetShipmentsPayload, Shipment>( getShipments, { page, perPage, search: search.trim() || undefined }, () => setLoadingShipments(( value ) => !value ));

    if( response !== null ) {
      setPage( response.page );
      setTotal( response.total );
      setPerPage( response.perPage );

      setShipments( response.results );
    }
  };

  const handleToggleOpenShipmentModal = () => setOpenShipmentModal(( value ) => !value );

  useEffect(() => {
    loadShipments();
    // eslint-disable-next-line
  }, [ page, perPage, pathname, search ]);

  useEffect(() => {
    if( !didMountModalEffect.current ) {
      didMountModalEffect.current = true;
      return;
    }

    if( !openShipmentModal ) loadShipments().then(() => handleShipment( null ));
    // eslint-disable-next-line
  }, [ openShipmentModal ]);

  return useMemo(() => ({
    page,
    perPage,
    total,
    search,
    shipments,
    loadingShipments,
    shipment,
    openShipmentModal,
    changePage,
    changeSearch,
    handleToggleOpenShipmentModal,
    handleShipment,
    // eslint-disable-next-line
  }), [
    page,
    perPage,
    total,
    search,
    shipments,
    loadingShipments,
    shipment,
    openShipmentModal,
  ]);
};
